import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { GATE_APPROVED_SURFACE, gatePillClass } from '@/versions/v1/modules/advisory/gate-ui';
import { useEscalationDecisions } from '@/versions/v1/shared/EscalationDecisionProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { formatHours } from '@/versions/v1/modules/maintenance/Downtime.transform';
import { GmMiniRow, LandingStripTabs } from './GmLeadBand';
import { GmCard } from './GmTabs';
import { DecisionField, NEUTRAL_PILL } from './LandingDecisionGate';
import { pendingFmeaReviewCount } from './Scene.transform';
import type { TextSegment } from './MaintenanceTabs.transform';
import {
  MAINT_SUBTITLE,
  maintCrew,
  maintInsight,
  maintLedger,
  maintMtbfBars,
  maintPmCard,
  maintRegister,
  maintReliabilityBand,
  maintStripCells,
  maintWaiting,
  maintWorkOrders,
  type LedgerRow,
  type MaintTabId,
  type StopGate,
  type WaitingItem,
} from './MaintenanceLanding.transform';

// ---- Primitives on the comp's recipe ------------------------------------------------

const PILL_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

/** Soft status pill on the state tokens (comp `.pill`): colour marks state only. */
function pillClass(tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral'): string {
  switch (tone) {
    case 'ok':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]');
    case 'warn':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]');
    case 'bad':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]');
    case 'info':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-info)_12%,transparent)] text-[var(--state-info)]');
    default:
      return NEUTRAL_PILL;
  }
}

const COND_DOT: Record<LedgerRow['cond'], string> = {
  bad: 'bg-[var(--state-blocked)]',
  warn: 'bg-[var(--state-warning)]',
  ok: 'bg-[var(--state-online)]',
};

const GO_LINK =
  'inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[12.5px] font-semibold text-ink-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

/** The comp's underlined inline link (`.lnk`): Case ↗, Approvals ↗. */
const INLINE_LINK =
  'inline-flex items-center gap-0.5 whitespace-nowrap border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary hover:border-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

/** The provenance chip the PM card and the crew card both wear: the figures
 *  come from `[syn]` estimates the build seeded (the landing's idiom). */
function SynChip({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-1.5 text-[10.5px] leading-4 text-muted',
        className,
      )}
    >
      [syn]
    </span>
  );
}

const CAPS = 'text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';

const TH = cn('whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-1.5 text-left', CAPS);
const TD = 'border-b border-[var(--color-divider)] px-2 py-2 align-top';

function GoLink({ to, children, ...rest }: { to: string; children: string } & Record<`data-${string}`, string | boolean>) {
  return (
    <Link to={to} className={GO_LINK} {...rest}>
      {children}
      <ArrowUpRight className="size-3.5" aria-hidden="true" />
    </Link>
  );
}

function Segments({ parts }: { parts: readonly TextSegment[] }) {
  return (
    <>
      {parts.map((part, index) =>
        part.code ? (
          <span key={index} className="whitespace-nowrap font-mono text-[11.5px] text-ink-primary">
            {part.text}
          </span>
        ) : part.strong ? (
          <span key={index} className="font-semibold text-ink-primary">
            {part.text}
          </span>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

// ---- The lead band ----------------------------------------------------------------

function PmComplianceCard({ state, href }: { state: SessionState; href: string }) {
  const card = maintPmCard(state);
  return (
    <Link
      to={href}
      data-maint-pm
      aria-label={card ? `PM compliance · ${card.figure} · Maintenance` : 'PM compliance · Maintenance'}
      className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.07em] text-muted">PM compliance</span>
        <span className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-ink-primary">
          <span className="hidden min-[1400px]:inline">Maintenance</span>
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </span>
      </span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        <span data-maint-pm-figure>{card?.figure ?? 'unmeasured'}</span>
        {card ? <span className="text-xs font-semibold tracking-normal text-muted">{card.unit}</span> : null}
      </span>
      {card ? (
        <span className="text-xs leading-snug tabular-nums text-muted" data-maint-pm-comparison>
          <span className="font-semibold text-ink-secondary">{card.lead}</span>
          {card.rest}
        </span>
      ) : null}
      <span className="mt-auto flex flex-wrap items-center gap-1 pt-1">
        <ActorChip actor="System" />
        <SynChip />
      </span>
    </Link>
  );
}

function MaintLeadBand({
  state,
  tab,
  onTabChange,
  pmHref,
}: {
  state: SessionState;
  tab: MaintTabId;
  onTabChange: (tab: MaintTabId) => void;
  pmHref: string;
}) {
  return (
    <Commentable name="Kpis">
      <div className="grid grid-cols-12 gap-3" data-maint-lead-band>
        <GlassCard
          as="nav"
          tone="matte"
          aria-label="Landing views"
          className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
        >
          <LandingStripTabs
            cells={maintStripCells(state)}
            selected={tab}
            onSelect={onTabChange}
            dataPrefix="maint"
            panelId="maint-landing-panel"
            columnsClassName="sm:grid-cols-3"
          />
        </GlassCard>
        <PmComplianceCard state={state} href={pmHref} />
      </div>
    </Commentable>
  );
}

// ---- Equipment health --------------------------------------------------------------

function LedgerLine({ row }: { row: LedgerRow }) {
  return (
    <tr className="[&:last-child>td]:border-b-0" data-maint-ledger-row={row.id} data-maint-cond={row.cond}>
      <td className={cn(TD, 'w-[30px] pl-4 text-center text-xs font-bold tabular-nums', row.rank === 1 ? 'text-[var(--color-accent-500)]' : 'text-muted')}>
        {row.rank}
      </td>
      <td className={cn(TD, 'w-[30%]')}>
        <div className={CAPS} data-maint-area>{row.area}</div>
        <div className="flex items-baseline gap-[7px] font-semibold text-ink-primary">
          <span aria-hidden="true" className={cn('inline-block size-[7px] shrink-0 translate-y-[-1px] rounded-full', COND_DOT[row.cond])} />
          <span className="min-w-0">{row.name}</span>
        </div>
        {row.alert ? (
          <div className="mt-[3px]">
            <span className={pillClass('bad')} data-maint-alert>{row.alert}</span>
          </div>
        ) : null}
      </td>
      <td className={cn(TD, 'text-ink-secondary')}>
        <div>
          <Segments parts={row.now} />
        </div>
        {row.nowSub ? <div className="mt-px text-xs leading-snug text-muted">{row.nowSub}</div> : null}
      </td>
      <td className={cn(TD, 'w-[20%]')}>
        {row.next ? (
          <div className={row.next.past ? 'text-muted' : 'text-ink-primary'} data-maint-next={row.next.past ? 'past' : 'ahead'}>
            {row.next.text}
          </div>
        ) : null}
      </td>
      <td className={cn(TD, 'pr-4 text-right')}>
        <span className={pillClass(row.trend.tone)} data-maint-trend>{row.trend.word}</span>
      </td>
    </tr>
  );
}

function LedgerCard({ state, conditionHref }: { state: SessionState; conditionHref: string }) {
  const ledger = maintLedger(state);
  const [open, setOpen] = useState(false);
  const rows = open ? [...ledger.first, ...ledger.rest] : ledger.first;
  return (
    <GmCard
      flush
      title="Equipment condition"
      meta={
        <>
          <span>alerts first, then condition</span>
          <GoLink to={conditionHref} data-maint-condition-link>Condition monitoring</GoLink>
        </>
      }
      className="h-full"
      data-maint-ledger
    >
      <table className="w-full border-collapse text-[13px] tabular-nums">
        <thead>
          <tr>
            <th className={cn(TH, 'pl-4')} aria-label="Rank" />
            <th className={TH}>Equipment</th>
            <th className={TH}>Condition now</th>
            <th className={TH}>Next</th>
            <th className={cn(TH, 'pr-4 text-right')}>Trend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <LedgerLine key={row.id} row={row} />
          ))}
        </tbody>
      </table>
      {ledger.rest.length > 0 ? (
        <button
          type="button"
          className="flex w-full items-baseline justify-between gap-2 border-t border-[var(--color-divider)] px-3 pb-3 pt-2.5 text-left text-[12.5px] text-muted transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          data-maint-ledger-more
        >
          <span className="min-w-0 [text-wrap:balance]" data-maint-ledger-more-label>
            {open ? null : (
              <>
                <span className="font-semibold text-ink-secondary">{ledger.restLabel}</span>
                {` · ${ledger.rest.map((row) => row.name).join(' · ')}`}
              </>
            )}
          </span>
          <span className="shrink-0 font-semibold text-ink-primary">{open ? 'Fewer ▴' : 'Show ▾'}</span>
        </button>
      ) : null}
    </GmCard>
  );
}

function StopGateBox({ gate }: { gate: StopGate }) {
  return (
    <div
      className={cn(
        'mt-2 flex flex-col gap-2 rounded-xl border py-2 pl-3 pr-2 transition-colors duration-300',
        gate.recorded ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
      )}
      data-maint-gate={gate.recorded ? 'recorded' : 'open'}
      data-maint-gate-card={gate.cardId}
    >
      <p className="text-[12.5px] text-ink-secondary [text-wrap:pretty]">
        <span className="font-semibold text-ink-primary">{gate.question.lead}</span>
        {gate.question.rest}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {gate.seats.map((seat) => (
          <DecisionField key={seat.role} label={seat.role}>
            <ActorChip actor="Human">{seat.holder}</ActorChip>
          </DecisionField>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-maint-gate-state>
        {gate.state.map((line) => (
          <span key={line.text} className={line.tone === 'ok' ? gatePillClass('approved') : NEUTRAL_PILL}>
            {line.text}
          </span>
        ))}
      </div>
    </div>
  );
}

function WaitingEntry({
  item,
  rank,
  caseHref,
  approvalsHref,
}: {
  item: WaitingItem;
  rank: number;
  caseHref: (caseId: string) => string;
  approvalsHref: string;
}) {
  return (
    <div
      className="grid grid-cols-[22px_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--color-divider)] py-2.5 first:pt-0.5 last:border-b-0 last:pb-0"
      data-maint-waiting-item={item.id}
    >
      <span className={cn('pt-px text-center text-xs font-bold tabular-nums', rank === 1 ? 'text-[var(--color-accent-500)]' : 'text-muted')}>{rank}</span>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2.5">
          <span className="text-[13.5px] font-semibold text-ink-primary">{item.title}</span>
          <span className={pillClass(item.due.tone)} data-maint-waiting-due>{item.due.text}</span>
        </div>
        {item.fin.length > 0 || item.caseLink || item.approvals ? (
          <div className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">
            <Segments parts={item.fin} />
            {item.caseLink ? (
              <>
                {' · '}
                <Link to={caseHref(item.caseLink.caseId)} className={INLINE_LINK} data-maint-case-link={item.caseLink.caseId}>
                  Case
                  <ArrowUpRight className="size-3" aria-hidden="true" />
                </Link>
              </>
            ) : null}
            {item.approvals ? (
              <Link to={approvalsHref} className={INLINE_LINK} data-maint-approvals-link>
                Approvals
                <ArrowUpRight className="size-3" aria-hidden="true" />
              </Link>
            ) : null}
          </div>
        ) : null}
        {item.checks && item.checks.length > 0 ? (
          <div className="mt-1.5 grid grid-cols-[44px_minmax(0,1fr)] gap-x-2 gap-y-[3px] text-xs tabular-nums" data-maint-release-clock>
            {item.checks.map((check) => (
              <div key={check.at} className="contents" data-maint-check={check.at}>
                <span className="font-semibold text-muted">{check.at}</span>
                <span className={check.past ? 'text-muted' : 'text-ink-primary'}>
                  {check.text}
                  {check.workOrderId ? (
                    <>
                      {' · '}
                      <span className="whitespace-nowrap font-mono text-[11px] text-muted">{check.workOrderId}</span>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {item.gate ? <StopGateBox gate={item.gate} /> : null}
      </div>
    </div>
  );
}

function WaitingCard({ state, projectSlug }: { state: SessionState; projectSlug: string }) {
  const { records } = useEscalationDecisions();
  const { can } = useRole();
  const items = maintWaiting(state, {
    record: records.get('esc-1'),
    canReview: can('fmea:review'),
    pendingReviews: pendingFmeaReviewCount(),
  });
  const advisory = projectSidebarHref(projectSlug, 'advisory');
  return (
    <GmCard
      title="Waiting on you"
      meta={<span className={NEUTRAL_PILL} data-maint-waiting-count>{items.length}</span>}
      className="h-full"
      data-maint-waiting
    >
      <div className="flex flex-col">
        {items.map((item, index) => (
          <WaitingEntry
            key={item.id}
            item={item}
            rank={index + 1}
            caseHref={(caseId) => `${advisory}/case/${caseId}`}
            approvalsHref={`${advisory}?tab=approvals`}
          />
        ))}
      </div>
    </GmCard>
  );
}

function EquipmentView({ state, projectSlug }: { state: SessionState; projectSlug: string }) {
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <LedgerCard state={state} conditionHref={`${projectSidebarHref(projectSlug, 'maintenance')}?tab=cond`} />
        </div>
      </Commentable>
      <Commentable name="Secondary">
        <div className="col-span-12 lg:col-span-5">
          <WaitingCard state={state} projectSlug={projectSlug} />
        </div>
      </Commentable>
    </div>
  );
}

// ---- Work orders ---------------------------------------------------------------------

const DUE_TONE = {
  bad: 'font-semibold text-[var(--state-blocked)]',
  ink: 'text-ink-primary',
} as const;

/** The landing's work list is `/maintenance`'s PM schedule (#734): one row per
 *  open PM instance, the columns that route's schedule tab draws (its due date,
 *  asset, task, crew and hours), with no landing-only WO id or priority. */
function WorkOrdersCard({ state, pmHref }: { state: SessionState; pmHref: string }) {
  const rows = maintWorkOrders(state);
  return (
    <GmCard
      flush
      title="Work orders"
      meta={
        <>
          <span>overdue first, then due date</span>
          <GoLink to={pmHref} data-maint-pm-schedule-link>PM schedule</GoLink>
        </>
      }
      className="h-full"
      data-maint-work-orders
    >
      <table className="w-full table-fixed border-collapse text-[13px] tabular-nums">
        <colgroup>
          <col className="w-[68px]" />
          <col className="w-[168px]" />
          <col />
          <col className="w-[128px]" />
          <col className="w-[62px]" />
          <col className="w-[106px]" />
        </colgroup>
        <thead>
          <tr>
            <th className={cn(TH, 'pl-4')}>Due</th>
            <th className={TH}>Asset</th>
            <th className={TH}>Task</th>
            <th className={TH}>Crew</th>
            <th className={cn(TH, 'text-right')}>Hrs</th>
            <th className={cn(TH, 'pr-4 text-right')}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} title={row.id} className="[&:last-child>td]:border-b-0" data-maint-wo={row.id}>
              <td className={cn(TD, 'whitespace-nowrap pl-4', DUE_TONE[row.dueTone])} data-maint-wo-due={row.dueTone}>
                {row.due}
              </td>
              <td className={cn(TD, 'font-semibold text-ink-primary')}>{row.asset}</td>
              <td className={cn(TD, 'text-ink-secondary [text-wrap:pretty]')}>{row.task}</td>
              {/* A long crew name wraps inside its chip rather than truncating (comp `.wot .actor`). */}
              <td className={cn(TD, '[&_[data-actor-chip]>span]:whitespace-normal [&_[data-actor-chip]]:whitespace-normal [&_[data-actor-chip]]:text-left')}>
                <ActorChip actor="Human">{row.crew}</ActorChip>
              </td>
              <td className={cn(TD, 'whitespace-nowrap text-right text-ink-secondary')}>{formatHours(row.hours)}</td>
              <td className={cn(TD, 'pr-4 text-right')}>
                <span className={pillClass(row.statusTone)}>{row.statusLabel}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GmCard>
  );
}

function CrewCard({ state }: { state: SessionState }) {
  const crew = maintCrew(state);
  return (
    <GmCard title="Crew on shift" meta={crew.window} className="h-full" data-maint-crew>
      <div className="flex items-baseline gap-2.5 tabular-nums">
        <span className="whitespace-nowrap text-[22px] font-semibold leading-tight text-ink-primary" data-maint-crew-booked>
          {formatHours(crew.bookedHours)}
        </span>
        <span className="text-xs text-muted">
          <span className="font-semibold text-ink-secondary">booked</span>
          {` · ${crew.technicianCount} technician${crew.technicianCount === 1 ? '' : 's'} × ${formatHours(crew.averageHours)} average`}
          <SynChip className="ml-1.5 align-middle" />
        </span>
      </div>
      <div className="mt-1.5">
        {crew.rows.map((row) => (
          <div
            key={row.name}
            className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-2.5 border-b border-[var(--color-divider)] py-2 text-[12.5px] tabular-nums last:border-b-0"
            data-maint-crew-row={row.name}
          >
            <span>
              <ActorChip actor="Human">{row.name}</ActorChip>
            </span>
            <span className="text-right text-ink-secondary">
              {row.lead}
              {row.more.map((line) => (
                <span key={line} className="block text-xs text-muted">
                  {line}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </GmCard>
  );
}

function WorkOrdersView({ state, pmHref }: { state: SessionState; pmHref: string }) {
  // The seven-column table needs the full row below 1400 px (its fixed columns
  // leave the task column no room at 8 of 12 there); the crew card then follows it.
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 min-[1400px]:col-span-8">
          <WorkOrdersCard state={state} pmHref={pmHref} />
        </div>
      </Commentable>
      <Commentable name="Secondary">
        <div className="col-span-12 min-[1400px]:col-span-4">
          <CrewCard state={state} />
        </div>
      </Commentable>
    </div>
  );
}

// ---- Reliability ---------------------------------------------------------------------

const BAR_TONE = {
  ok: 'bg-[var(--state-online)]',
  warn: 'bg-[var(--state-warning)]',
  bad: 'bg-[var(--state-blocked)]',
} as const;

function MtbfCard({ state, href }: { state: SessionState; href: string }) {
  const bars = maintMtbfBars(state);
  return (
    <GmCard
      title="MTBF actual vs target"
      meta={
        <>
          <span>bar = actual · tick = target · worst gap first</span>
          <GoLink to={href} data-maint-mtbf-link>MTBF / MTTR</GoLink>
        </>
      }
      className="h-full"
      data-maint-mtbf
    >
      {bars.map((bar) => (
        <div
          key={bar.id}
          className="grid grid-cols-[minmax(0,190px)_minmax(0,1fr)_132px] items-center gap-x-3 border-b border-[var(--color-divider)] py-1.5 text-[12.5px] tabular-nums last:border-b-0"
          data-maint-mtbf-row={bar.id}
        >
          <span className="font-medium text-ink-primary">{bar.name}</span>
          <span className="relative h-2.5 rounded-[5px] bg-[var(--hairline)]" title={`${bar.actual} w actual · ${bar.target} w target`}>
            <i className={cn('absolute inset-y-0 left-0 rounded-[5px]', BAR_TONE[bar.tone])} style={{ width: `${bar.barPct}%` }} />
            <u className="absolute -top-1 h-[18px] w-0.5 rounded-[1px] bg-[var(--ink-secondary)]" style={{ left: `calc(${bar.tickPct}% - 1px)` }} />
          </span>
          <span className="whitespace-nowrap text-right text-ink-secondary">
            <span className="font-semibold text-ink-primary">{`${bar.actual} w`}</span>
            {` / ${bar.target} w · ${bar.pct}%`}
          </span>
        </div>
      ))}
    </GmCard>
  );
}

function RegisterCard({ state }: { state: SessionState }) {
  const rows = maintRegister(state);
  const insight = maintInsight(state);
  return (
    <GmCard title="Improvement register" meta="top 3 with a business case" className="h-full" data-maint-register>
      {rows.map((row) => (
        <div
          key={row.id}
          className="grid grid-cols-[62px_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--color-divider)] py-2.5 first:pt-1"
          data-maint-register-row={row.id}
        >
          <span className="pt-0.5 font-mono text-xs font-semibold text-muted">{row.id}</span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-ink-primary">{row.title}</div>
            <div className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">{row.line}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={pillClass(row.status === 'Approved' ? 'ok' : 'neutral')}>{row.status}</span>
              <span className="text-xs tabular-nums text-muted">{row.meta}</span>
            </div>
          </div>
        </div>
      ))}
      {insight ? (
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 pt-2.5 text-[13px]" data-maint-insight>
          <ActorChip actor="Engine" />
          <div className="min-w-0">
            <p className="font-semibold text-ink-primary">{insight.headline}</p>
            <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">{insight.body}</p>
          </div>
        </div>
      ) : null}
    </GmCard>
  );
}

function ReliabilityView({ state, projectSlug }: { state: SessionState; projectSlug: string }) {
  const downtime = projectSidebarHref(projectSlug, 'downtime');
  const links = { mtbf: `${downtime}?tab=mtbf`, ratio: `${downtime}?tab=ratio` };
  return (
    <div className="space-y-3">
      <div data-maint-band>
        <GmMiniRow items={maintReliabilityBand(state, links)} />
      </div>
      <div className="grid grid-cols-12 gap-3">
        <Commentable name="Primary">
          <div className="col-span-12 lg:col-span-7">
            <MtbfCard state={state} href={links.mtbf} />
          </div>
        </Commentable>
        <Commentable name="Secondary">
          <div className="col-span-12 lg:col-span-5">
            <RegisterCard state={state} />
          </div>
        </Commentable>
      </div>
    </div>
  );
}

// ---- Composition ------------------------------------------------------------------

/**
 * The Maintenance landing (polish.P14 #701, locked comp
 * `landing-maintenance.html` A, finished with B's BM01 release-check clock and
 * C's plant-area small caps): the GM landing's anatomy at equipment scope.
 * Three strip cells are the tab bar, Equipment health is the default view, and
 * PM compliance steps onward to Maintenance.
 */
export function MaintenanceLanding({ state, projectSlug }: { state: SessionState; projectSlug: string }) {
  const [tab, setTab] = useState<MaintTabId>('equipment');
  const pmHref = `${projectSidebarHref(projectSlug, 'maintenance')}?tab=pm`;
  return (
    <div className="space-y-3" data-maint-landing>
      <p className="-mt-3 text-sm text-muted">{MAINT_SUBTITLE}</p>
      <MaintLeadBand state={state} tab={tab} onTabChange={setTab} pmHref={pmHref} />
      {/* Each view anchors its columns at the Body level (Primary, Secondary),
          the paths `anchors.ts` registers. */}
      <div id="maint-landing-panel" role="tabpanel" aria-labelledby={`maint-tab-${tab}`}>
        {tab === 'equipment' ? <EquipmentView state={state} projectSlug={projectSlug} /> : null}
        {tab === 'workorders' ? <WorkOrdersView state={state} pmHref={pmHref} /> : null}
        {tab === 'reliability' ? <ReliabilityView state={state} projectSlug={projectSlug} /> : null}
      </div>
    </div>
  );
}
